import type { ThemeConfig } from "antd";
import { mergeDeep } from "remeda";

import useIsMobile from "hooks/useIsMobile";

import colors from "./colors";
import seedtime, { fontSize, fontWeight, radius, space } from "./seedtime";

const isTest = process.env.RAILS_ENV === "test";

export const SCREEN_SM_MAX = 767;
export const MOBILE_MAX_SCREEN_SIZE = SCREEN_SM_MAX;
export const NON_MOBILE_MIN_SCREEN_SIZE = MOBILE_MAX_SCREEN_SIZE + 1;

// Components listed here are already upgraded to v6
const theme: ThemeConfig = {
  components: {
    Badge: {
      colorTextLightSolid: colors.black,
      // Indicator background color
      colorError: colors.accent,
      indicatorHeightSM: 18
    },
    Button: {
      paddingInlineSM: space[150],
      colorTextDisabled: colors.midGrey,
      colorBgContainerDisabled: colors.pageBackground,
      defaultBorderColor: colors.lightGrey,
      fontSizeLG: fontSize.bodySmall
    },
    Carousel: {
      arrowSize: 48
    },
    DatePicker: {
      paddingXXS: space["025"]
    },
    Dropdown: {
      borderRadiusLG: radius.small,
      controlPaddingHorizontal: space[100],
      fontSize: fontSize.caption,
      marginXXS: space[0],
      paddingXXS: space[0]
    },
    Empty: {
      margin: space[100]
    },
    Input: {
      colorTextPlaceholder: colors.midGrey,
      controlHeightLG: 44,
      hoverBorderColor: colors.primary,
      paddingBlockLG: 7,
      paddingInlineLG: 13
    },
    Modal: {
      headerBg: colors.pageBackground,
      paddingContentHorizontalLG: space[0],
      paddingMD: space[0]
    },
    Popover: {
      paddingSM: space[100],
      fontSize: fontSize.caption
    },
    Select: {
      borderRadiusSM: radius.medium,
      controlHeight: 44,
      fontSize: fontSize.body,
      fontSizeLG: fontSize.body,
      multipleItemBorderColor: colors.lightGrey,
      optionActiveBg: colors.selectHover,
      optionPadding: 10
    },
    Statistic: {
      colorTextHeading: colors.black,
      colorTextDescription: colors.midGrey,
      contentFontSize: 24,
      titleFontSize: fontSize.bodySmall
    },
    Progress: { defaultColor: colors.primary },
    Timeline: {}
  },
  token: {
    borderRadius: radius.large,
    colorIcon: colors.primary,
    colorLink: colors.primary,
    colorPrimary: colors.primary,
    colorError: colors.error,
    colorInfo: colors.midGrey,
    colorSuccessText: colors.primary,
    colorTextSecondary: colors.midGrey,
    fontFamily: seedtime["text-font"],
    fontWeightStrong: fontWeight.semibold,
    fontSize: fontSize.bodySmall,
    screenSMMax: SCREEN_SM_MAX,
    ...(isTest && {
      motionDurationFast: "0.1ms",
      motionDurationMid: "0.1ms",
      motionDurationSlow: "0.1ms"
    })
  }
} as const;

export const gardenPlanTheme: ThemeConfig = mergeDeep(theme, {
  components: {
    Form: {
      fontSize: fontSize.caption,
      itemMarginBottom: space[150],
      labelHeight: "100%",
      verticalLabelPadding: space[0]
    },
    Select: {
      controlHeight: 36,
      fontSize: fontSize.bodySmall,
      optionLineHeight: "20px"
    }
  }
});

const mobileTheme = mergeDeep(theme, {
  components: {
    Button: {
      fontSizeLG: fontSize.bodySmall
    },
    Select: {
      fontSize: fontSize.bodySmall
    }
  },
  token: { fontSize: fontSize.bodySmall }
});

export const useAntdTheme = () => {
  const isMobile = useIsMobile();

  return isMobile ? mobileTheme : theme;
};

export default theme;
